import React from 'react';
import Select from 'react-select';

/**
 * Filter bar for the Expense Voucher list.
 *
 * Replaces the five per-role status dropdowns (HOD Status, General Manager Status,
 * Audit Status, Group Head Status, Finance Status). Those described a chain that no
 * longer exists: levels are configurable now, a level can hold several approvers, and
 * a level like "Procurement Desk" had no dropdown at all. The questions people
 * actually ask — *where* is this voucher, and *who* is holding it — are the two
 * controls here.
 *
 * Filters are held by the parent in the URL, so this component is fully controlled:
 * it edits a local draft and hands it back on Apply. Typing into a field does not
 * refetch, which matters on a list this size.
 */

export type VoucherFilters = {
  search: string;
  email: string;
  start_date: string;
  end_date: string;
  level_id: string;
  approver_email: string;
  workflow_status: string;
  mine: boolean;
};

export const emptyVoucherFilters: VoucherFilters = {
  search: '',
  email: '',
  start_date: '',
  end_date: '',
  level_id: '',
  approver_email: '',
  workflow_status: '',
  mine: false,
};

/** Where the list's filters are parked while the user is away on the edit page. */
export const VOUCHER_FILTER_STORAGE_KEY = 'skynet.voucherListFilters';

export const WORKFLOW_STATUSES: { value: string; label: string }[] = [
  { value: 'in_progress', label: 'In progress' },
  { value: 'returned', label: 'Returned for changes' },
  { value: 'approved', label: 'Approved' },
  { value: 'awaiting_disbursement', label: 'Awaiting disbursement' },
  { value: 'disbursed', label: 'Disbursed' },
];

export function voucherFiltersFromParams(params: URLSearchParams): VoucherFilters {
  return {
    search: params.get('search') ?? '',
    email: params.get('email') ?? '',
    start_date: params.get('start_date') ?? '',
    end_date: params.get('end_date') ?? '',
    level_id: params.get('level_id') ?? '',
    approver_email: params.get('approver_email') ?? '',
    workflow_status: params.get('workflow_status') ?? '',
    mine: params.get('mine') === '1',
  };
}

/**
 * Only non-empty filters make it into the URL, so a cleared filter bar leaves a clean
 * address and `countActiveVoucherFilters` can just count the keys.
 */
export function voucherFiltersToParams(filters: VoucherFilters, page?: string | number): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'mine') {
      if (value) params.set('mine', '1');
      return;
    }
    if (value) params.set(key, String(value));
  });

  if (page && String(page) !== '1') params.set('page', String(page));

  return params;
}

export function countActiveVoucherFilters(filters: VoucherFilters): number {
  return Object.entries(filters).filter(([, value]) => value !== '' && value !== false).length;
}

type Level = {
  id: number | string;
  name: string;
  position?: number;
  is_active?: boolean;
  approvers?: { email: string; name?: string }[];
};

type Props = {
  /** The filters currently in force — what the list on screen is actually showing. */
  value: VoucherFilters;
  /** Levels from GET /voucher-workflow/levels, approvers included. */
  levels: Level[];
  loading?: boolean;
  onApply: (filters: VoucherFilters) => void;
};

/** react-select, dressed to match the rest of the admin surface. */
const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    fontSize: 13,
    borderColor: state.isFocused ? '#E04343' : '#E5E7EB',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(224,67,67,0.10)' : 'none',
    '&:hover': { borderColor: '#E04343' },
  }),
  menu: (base: any) => ({ ...base, fontSize: 13, zIndex: 30 }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#E04343' : state.isFocused ? '#FDECEC' : 'white',
    color: state.isSelected ? 'white' : '#111827',
  }),
  groupHeading: (base: any) => ({
    ...base,
    fontSize: 10,
    letterSpacing: '0.08em',
    color: '#9CA3AF',
  }),
  placeholder: (base: any) => ({ ...base, color: '#9CA3AF' }),
};

const fieldClass =
  'w-full rounded-[10px] border border-gray-200 bg-white px-3 py-[8px] text-[13px] text-gray-900 ' +
  'outline-none focus:border-[#E04343] focus:ring-[3px] focus:ring-[#E04343]/10';

const labelClass = 'mb-1 block text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400';

function VoucherFilterBar({ value, levels, loading, onApply }: Props) {
  const [draft, setDraft] = React.useState<VoucherFilters>(value);
  const [expanded, setExpanded] = React.useState<boolean>(
    // Open the panel if the user arrives with detail filters already set — otherwise
    // the chips would be the only clue that a hidden control is doing something.
    () => Boolean(value.level_id || value.approver_email || value.workflow_status || value.email || value.start_date || value.end_date),
  );

  // The URL is the source of truth; a back-navigation or a chip removal changes it
  // underneath us and the draft has to follow.
  React.useEffect(() => setDraft(value), [value]);

  const activeLevels = React.useMemo(
    () => levels.filter((l) => l.is_active !== false).sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [levels],
  );

  const levelOptions = React.useMemo(
    () => activeLevels.map((l) => ({ value: String(l.id), label: l.name })),
    [activeLevels],
  );

  /**
   * Approvers for the selected level, or every approver grouped by level when no
   * level is picked — the same person can sit on two levels, so the grouping is what
   * makes the list readable.
   */
  const approverOptions = React.useMemo(() => {
    const selected = draft.level_id
      ? activeLevels.filter((l) => String(l.id) === String(draft.level_id))
      : activeLevels;

    return selected
      .filter((l) => (l.approvers ?? []).length > 0)
      .map((l) => ({
        label: l.name,
        options: (l.approvers ?? []).map((a) => ({
          value: a.email,
          label: a.name ? `${a.name} — ${a.email}` : a.email,
        })),
      }));
  }, [activeLevels, draft.level_id]);

  const selectedApproverOption = React.useMemo(() => {
    for (const group of approverOptions) {
      const hit = group.options.find((o) => o.value === draft.approver_email);
      if (hit) return hit;
    }
    // The approver may sit on a level other than the one now selected — still show it
    // rather than silently blanking the field.
    return draft.approver_email ? { value: draft.approver_email, label: draft.approver_email } : null;
  }, [approverOptions, draft.approver_email]);

  const activeCount = countActiveVoucherFilters(value);

  const apply = (next: VoucherFilters) => {
    setDraft(next);
    onApply(next);
  };

  const patchDraft = (patch: Partial<VoucherFilters>) => setDraft((d) => ({ ...d, ...patch }));

  /** Human-readable summary of what is in force, one removable chip per filter. */
  const chips = React.useMemo(() => {
    const out: { key: keyof VoucherFilters; label: string }[] = [];

    if (value.mine) out.push({ key: 'mine', label: 'Waiting on me' });
    if (value.search) out.push({ key: 'search', label: `Search: ${value.search}` });
    if (value.level_id) {
      const level = activeLevels.find((l) => String(l.id) === String(value.level_id));
      out.push({ key: 'level_id', label: `At: ${level?.name ?? value.level_id}` });
    }
    if (value.approver_email) out.push({ key: 'approver_email', label: `With: ${value.approver_email}` });
    if (value.workflow_status) {
      const status = WORKFLOW_STATUSES.find((s) => s.value === value.workflow_status);
      out.push({ key: 'workflow_status', label: `Status: ${status?.label ?? value.workflow_status}` });
    }
    if (value.email) out.push({ key: 'email', label: `Raised by: ${value.email}` });
    if (value.start_date) out.push({ key: 'start_date', label: `From: ${value.start_date}` });
    if (value.end_date) out.push({ key: 'end_date', label: `To: ${value.end_date}` });

    return out;
  }, [value, activeLevels]);

  const removeChip = (key: keyof VoucherFilters) =>
    apply({ ...value, [key]: key === 'mine' ? false : '' } as VoucherFilters);

  return (
    <div className="mt-4 rounded-[14px] border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      {/* Primary row — search, the one-click queue, and the panel toggle. */}
      <div className="flex flex-wrap items-center gap-2 p-3">
        <div className="relative min-w-[200px] flex-1">
          <input
            type="text"
            value={draft.search}
            onChange={(e) => patchDraft({ search: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply(draft);
            }}
            placeholder="Search voucher ID, department, name or email"
            className={fieldClass + ' pl-9'}
          />
          <svg
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
          </svg>
        </div>

        {/* The queue an approver opens this page for. One click, not a filter panel. */}
        <button
          type="button"
          onClick={() => apply({ ...value, mine: !value.mine })}
          className={
            'rounded-[10px] border px-3 py-[8px] text-[13px] font-medium transition ' +
            (value.mine
              ? 'border-[#E04343] bg-[#E04343] text-white'
              : 'border-gray-200 bg-white text-gray-600 hover:border-[#E04343] hover:text-[#E04343]')
          }
        >
          Waiting on me
        </button>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={
            'flex items-center gap-2 rounded-[10px] border px-3 py-[8px] text-[13px] font-medium transition ' +
            (expanded ? 'border-gray-300 bg-gray-50 text-gray-900' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300')
          }
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" strokeLinejoin="round" />
          </svg>
          Filters
          {activeCount > 0 && (
            <span className="rounded-full bg-[#E04343] px-[6px] py-[1px] text-[10px] font-semibold text-white">
              {activeCount}
            </span>
          )}
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={() => apply(draft)}
          className="rounded-[10px] bg-[#E04343] px-4 py-[8px] text-[13px] font-medium text-white transition hover:bg-[#c93a3a] disabled:opacity-60"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {/* Detail panel. */}
      {expanded && (
        <div className="grid gap-3 border-t border-gray-100 p-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={labelClass}>Approval level</label>
            <Select
              styles={selectStyles}
              isClearable
              placeholder="Any level"
              options={levelOptions}
              value={levelOptions.find((o) => o.value === String(draft.level_id)) ?? null}
              onChange={(opt: any) =>
                // Changing the level narrows the approver list, so an approver from the
                // old level would become unreachable in the dropdown while still
                // filtering. Clear it.
                patchDraft({ level_id: opt?.value ?? '', approver_email: '' })
              }
            />
          </div>

          <div>
            <label className={labelClass}>Approver</label>
            <Select
              styles={selectStyles}
              isClearable
              placeholder={draft.level_id ? 'Anyone on this level' : 'Any approver'}
              options={approverOptions as any}
              value={selectedApproverOption}
              onChange={(opt: any) => patchDraft({ approver_email: opt?.value ?? '' })}
              noOptionsMessage={() => 'No approvers configured'}
            />
          </div>

          <div>
            <label className={labelClass}>Voucher status</label>
            <select
              className={fieldClass}
              value={draft.workflow_status}
              onChange={(e) => patchDraft({ workflow_status: e.target.value })}
            >
              <option value="">Any status</option>
              {WORKFLOW_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Raised by</label>
            <input
              type="text"
              className={fieldClass}
              placeholder="Creator email"
              value={draft.email}
              onChange={(e) => patchDraft({ email: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') apply(draft);
              }}
            />
          </div>

          <div>
            <label className={labelClass}>Raised from</label>
            <input
              type="date"
              className={fieldClass}
              value={draft.start_date}
              onChange={(e) => patchDraft({ start_date: e.target.value })}
            />
          </div>

          <div>
            <label className={labelClass}>Raised to</label>
            <input
              type="date"
              className={fieldClass}
              value={draft.end_date}
              onChange={(e) => patchDraft({ end_date: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* What is actually in force, and one click to drop any of it. */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-3 py-2">
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#FDECEC] py-[3px] pl-3 pr-[6px] text-[12px] text-[#B33232]"
            >
              {chip.label}
              <button
                type="button"
                aria-label={`Remove ${chip.label}`}
                onClick={() => removeChip(chip.key)}
                className="flex h-[15px] w-[15px] items-center justify-center rounded-full text-[#B33232] transition hover:bg-[#E04343] hover:text-white"
              >
                <svg className="h-[9px] w-[9px]" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 12 12">
                  <path d="m2 2 8 8M10 2l-8 8" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}

          <button
            type="button"
            onClick={() => apply({ ...emptyVoucherFilters })}
            className="ml-auto text-[12px] font-medium text-gray-500 underline-offset-2 hover:text-[#E04343] hover:underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

export default VoucherFilterBar;
